import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ticketService from '../services/ticketService';
import { useNotification } from './NotificationContext';
import { useAuth } from './AuthContext';

const ActiveChatsContext = createContext();

export const useActiveChats = () => {
  const context = useContext(ActiveChatsContext);
  if (!context) {
    throw new Error('useActiveChats deve ser usado dentro de um ActiveChatsProvider');
  }
  return context;
};

export const ActiveChatsProvider = ({ children }) => {
  const [activeChats, setActiveChats] = useState([]);
  const [compactChatOpen, setCompactChatOpen] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [queues, setQueues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  // Carregar tickets ativos do backend
  const loadActiveTickets = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const tickets = await ticketService.getActiveTickets({
        userId: user.id,
        queueIds: JSON.stringify(user.queues?.map(q => q.id) || [])
      });

      const chats = tickets.map(ticket => ({
        id: ticket.id,
        ticketId: ticket.id,
        name: ticket.contact?.name || ticket.contact?.number,
        number: ticket.contact?.number,
        avatar: ticket.contact?.profilePicUrl,
        status: ticket.status,
        queueId: ticket.queueId,
        queueName: ticket.queue?.name,
        queueColor: ticket.queue?.color,
        userId: ticket.userId,
        userName: ticket.user?.name,
        lastMessage: ticket.lastMessage,
        unreadMessages: ticket.unreadMessages || 0,
        lastActivity: new Date(ticket.updatedAt),
        whatsappId: ticket.whatsappId,
        whatsappName: ticket.whatsapp?.name
      }));

      setActiveChats(chats);
      
      // Calcular total de mensagens não lidas
      const totalUnread = chats.reduce((sum, chat) => sum + (chat.unreadMessages || 0), 0);
      setUnreadCount(totalUnread);
      
    } catch (error) {
      if (showError) {
        showError('Erro ao carregar conversas ativas');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, showError]);

  // Carregar filas disponíveis
  const loadQueues = useCallback(async () => {
    try {
      const queuesData = await ticketService.getQueues();
      setQueues(Array.isArray(queuesData) ? queuesData : []);
    } catch (error) {
      setQueues([]);
    }
  }, []);

  // Carregar dados iniciais
  useEffect(() => {
    if (user) {
      loadActiveTickets();
      loadQueues();
    }
  }, [user, loadActiveTickets, loadQueues]);

  // Atualizar tickets periodicamente
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      loadActiveTickets();
    }, 30000); // Atualizar a cada 30 segundos

    return () => clearInterval(interval);
  }, [user, loadActiveTickets]);

  // Adicionar conversa ativa (para novos tickets)
  const addActiveChat = useCallback((ticket) => {
    const chat = {
      id: ticket.id,
      ticketId: ticket.id,
      name: ticket.contact?.name || ticket.contact?.number,
      number: ticket.contact?.number,
      avatar: ticket.contact?.profilePicUrl,
      status: ticket.status,
      queueId: ticket.queueId,
      queueName: ticket.queue?.name,
      queueColor: ticket.queue?.color,
      userId: ticket.userId,
      userName: ticket.user?.name,
      lastMessage: ticket.lastMessage,
      unreadMessages: ticket.unreadMessages || 0,
      lastActivity: new Date(ticket.updatedAt),
      whatsappId: ticket.whatsappId,
      whatsappName: ticket.whatsapp?.name
    };

    setActiveChats(prev => {
      const exists = prev.find(c => c.id === chat.id);
      if (exists) {
        return prev.map(c => c.id === chat.id ? { ...c, ...chat } : c);
      }
      return [...prev, chat];
    });
  }, []);

  // Remover conversa ativa
  const removeActiveChat = useCallback((chatId) => {
    setActiveChats(prev => prev.filter(chat => chat.id !== chatId));
    if (selectedChatId === chatId) {
      setSelectedChatId(null);
      setCompactChatOpen(false);
    }
  }, [selectedChatId]);

  // Selecionar conversa
  const selectChat = useCallback((chatId) => {
    const chat = activeChats.find(c => c.id === chatId);
    if (!chat) return;

    setSelectedChatId(chatId);
    setCompactChatOpen(true);
    
    // Marcar mensagens como lidas
    setActiveChats(prev => 
      prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, unreadMessages: 0 }
          : chat
      )
    );
  }, [activeChats]);

  // Enviar mensagem
  const sendChatMessage = useCallback(async (chatId, messageText) => {
    try {
      const chat = activeChats.find(c => c.id === chatId);
      if (!chat) throw new Error('Chat não encontrado');

      const response = await ticketService.sendTicketMessage(chat.ticketId, {
        body: messageText
      });
      
      // Atualizar conversa com nova mensagem
      setActiveChats(prev => 
        prev.map(c => 
          c.id === chatId 
            ? { 
                ...c, 
                lastMessage: messageText,
                lastActivity: new Date()
              }
            : c
        )
      );
      
      if (showSuccess) {
        showSuccess('Mensagem enviada com sucesso!');
      }
      return response;
    } catch (error) {
      if (showError) {
        showError('Erro ao enviar mensagem');
      }
      throw error;
    }
  }, [activeChats, showSuccess, showError]);

  // Receber mensagem (para WebSocket)
  const receiveMessage = useCallback((message) => {
    setActiveChats(prev => 
      prev.map(chat => {
        if (chat.ticketId === message.ticketId) {
          const newUnreadCount = selectedChatId === chat.id ? 0 : (chat.unreadMessages || 0) + 1;
          return {
            ...chat,
            lastMessage: message.body,
            lastActivity: new Date(),
            unreadMessages: newUnreadCount
          };
        }
        return chat;
      })
    );
  }, [selectedChatId]);

  // Abrir/fechar chat compacto
  const toggleCompactChat = useCallback(() => {
    // Se o chat já está aberto e temos um chat atual, apenas alterna o estado
    if (compactChatOpen) {
      setCompactChatOpen(false);
      return;
    }

    // Se estiver fechado e não houver chat selecionado, mas houver chats ativos
    if (!compactChatOpen && activeChats.length > 0) {
      if (!selectedChatId) {
        // Selecionar o primeiro chat com mensagens não lidas ou o primeiro disponível
        const unreadChat = activeChats.find(chat => chat.unreadMessages > 0);
        const chatToSelect = unreadChat || activeChats[0];
        setSelectedChatId(chatToSelect.id);
      }
      setCompactChatOpen(true);
    } else if (activeChats.length === 0) {
      // Se não houver conversas, você pode redirecionar ou avisar
      setCompactChatOpen(true);
    } else {
      setCompactChatOpen(!compactChatOpen);
    }
  }, [compactChatOpen, activeChats, selectedChatId]);

  // Fechar chat compacto
  const closeCompactChat = useCallback(() => {
    setCompactChatOpen(false);
    setSelectedChatId(null);
  }, []);

  // Atualizar mensagens do chat
  const updateChatMessages = useCallback((chatId, messages) => {
    setActiveChats(prev => 
      prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, messages, lastActivity: new Date() }
          : chat
      )
    );
  }, []);

  // Transferir ticket para fila
  const transferToQueue = useCallback(async (ticketId, queueId) => {
    try {
      await ticketService.updateTicketStatus(ticketId, 'pending', null, queueId);
      await loadActiveTickets(); // Recarregar tickets
      if (showSuccess) {
        showSuccess('Ticket transferido para a fila com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao transferir ticket:', error);
      if (showError) {
        showError('Erro ao transferir ticket para a fila');
      }
    }
  }, [loadActiveTickets, showSuccess, showError]);

  // Assumir ticket
  const acceptTicket = useCallback(async (ticketId) => {
    try {
      await ticketService.updateTicketStatus(ticketId, 'open', user.id);
      await loadActiveTickets(); // Recarregar tickets
      if (showSuccess) {
        showSuccess('Ticket assumido com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao assumir ticket:', error);
      if (showError) {
        showError('Erro ao assumir ticket');
      }
    }
  }, [user, loadActiveTickets, showSuccess, showError]);

  // Obter chat atual selecionado
  const currentCompactChat = activeChats.find(chat => chat.id === selectedChatId) || null;

  const value = {
    activeChats,
    compactChatOpen,
    selectedChatId,
    unreadCount,
    currentCompactChat,
    queues,
    isLoading,
    addActiveChat,
    removeActiveChat,
    selectChat,
    sendChatMessage,
    receiveMessage,
    toggleCompactChat,
    closeCompactChat,
    updateChatMessages,
    transferToQueue,
    acceptTicket,
    loadActiveTickets,
    setCompactChatOpen
  };

  return (
    <ActiveChatsContext.Provider value={value}>
      {children}
    </ActiveChatsContext.Provider>
  );
};
